// Pure helpers for the delegation flow (L1b). Kept out of the component so the
// scope→resource mapping and duration math can be unit-tested directly.
//
// A delegation is stored as a (resource_kind, resource_id) pair — the same
// identity the meta-gate reads. The UI offers three friendly scopes that map
// onto the kinds whose owner the server can resolve (see factOwnerOf).

import type { PolicyDelegationRow } from '../../api/ws'

export type ScopeType = 'household' | 'agent' | 'host'
export type Duration = 'permanent' | 'hour' | 'day' | 'week' | 'month'

export const DURATION_LABELS: Record<Duration, string> = {
  permanent: 'Permanent',
  hour: 'For 1 hour',
  day: 'For 1 day',
  week: 'For 1 week',
  month: 'For 1 month',
}

const DURATION_MS: Record<Exclude<Duration, 'permanent'>, number> = {
  hour: 3600_000,
  day: 86_400_000,
  week: 7 * 86_400_000,
  month: 30 * 86_400_000,
}

// RFC3339 expiry for a duration, or undefined for permanent. `now` is injectable
// for deterministic tests.
export function expiryFor(d: Duration, now: number = Date.now()): string | undefined {
  if (d === 'permanent') return undefined
  return new Date(now + DURATION_MS[d]).toISOString()
}

// Map the friendly scope picker to a stored (resource_kind, resource_id).
// Returns null when a required target hasn't been chosen yet.
export function scopeToResource(
  scopeType: ScopeType,
  targetID: string,
): { kind: string; id: string } | null {
  if (scopeType === 'household') return { kind: 'organization', id: '*' }
  if (!targetID) return null
  if (scopeType === 'agent') return { kind: 'organization_position', id: targetID }
  if (scopeType === 'host') return { kind: 'host', id: targetID }
  return null
}

// Friendly one-liner for a stored delegation's resource, given name lookups.
export function describeResource(
  row: Pick<PolicyDelegationRow, 'resource_kind' | 'resource_id'>,
  positionName: (id: string) => string | undefined,
  hostName: (id: string) => string | undefined,
): string {
  if (row.resource_kind === 'organization') return 'Everything in this household'
  if (row.resource_kind === 'organization_position') {
    const name = positionName(row.resource_id)
    return name ? `Agent role: ${name}` : `Agent role ${row.resource_id}`
  }
  if (row.resource_kind === 'host') {
    const name = hostName(row.resource_id)
    return name ? `Host: ${name}` : `Host ${row.resource_id}`
  }
  return `${row.resource_kind}: ${row.resource_id}`
}
